<template>
  <article class="payment-dutch">
    <!-- 더치페이 헤더 -->
    <PaymentDutchHeader type="amount" />

    <div ref="listRefs" class="dutch-amount__list">
      <ul class="payment-group__ul list-style-none">
        <li v-for="amount in sortedDutchMenus" :key="amount.Id" @click="handleItemClick(amount.Id)">
          <PaymentDutchAmountItem
            :id="amount.Id"
            ref="itemRefs"
            v-model="amount.TotalPrice"
            type="input"
            :disabled="amount.IsPaid"
            :is-active="selectedAmount?.Id === amount.Id && !amount.IsPaid"
            @remove="handleRemoveClick"
            @cash="handleCashClick"
            @easy="handleEasyClick"
            @card="handleCardClick"
          />
        </li>
        <li>
          <PaymentDutchAmountItem type="add" @add="handleAddClick" />
        </li>
      </ul>
    </div>

    <div class="dutch-amount__keypad">
      <BaseKeypad v-model="keypad" :is-continuous="false" @keypad-click="handleKeypadClick" />
    </div>

    <footer class="dutch-amount__footer">
      <div class="dutch-amount__info">
        <div class="info__amount">
          <span class="info__text">{{ t('label.payment_amount') }}</span>
          <span class="info__number info__number--red">{{ formatCurrency(paymentStore.summary.amount) }}</span>
        </div>
      </div>
      <div class="dutch-amount__button-group">
        <BaseButton class="outline" :disabled="isPrevButtonDisabled" @button-click="handlePrevClick">
          <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
              <path
                id="arrow-left-solid"
                d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                transform="translate(-0.025 -63.925)"
              />
            </svg>
          </template>
          {{ t('button.prev') }}
        </BaseButton>
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseKeypad from '@/components/base/BaseKeypad.vue';
  import OrderComplete from '@/components/OrderComplete.vue';
  import PaymentDutchAmountItem from '@/components/PaymentDutchAmountItem.vue';
  import PaymentDutchHeader from '@/components/PaymentDutchHeader.vue';
  import { useOrder } from '@/composable/useOrder';
  import { usePayment } from '@/composable/usePayment';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import UserError from '@/error';
  import DutchAmount from '@/interfaces/DutchAmount';
  import { useCartStore } from '@/stores/cart';
  import { useCommonStore } from '@/stores/common';
  import { useModalStore } from '@/stores/modal';
  import { useOrderStore } from '@/stores/order';
  import { usePaymentStore } from '@/stores/payment';
  import { formatCurrency } from '@/utils';
  import { v4 as uuid } from 'uuid';
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useLogger } from 'vue-logger-plugin';
  import PaymentCardModal from './PaymentCardModal.vue';
  import PaymentEasyModal from './PaymentEasyModal.vue';

  const { t } = useI18n();
  const logger = useLogger();
  const commonStore = useCommonStore();
  const cartStore = useCartStore();
  const modalStore = useModalStore();
  const orderStore = useOrderStore();
  const paymentStore = usePaymentStore();

  const { openReceiptOrComplete } = useOrder();
  const { createPaymentSplit } = usePayment();

  // 더치페이 금액나눔 항목
  const dutchAmounts = ref<DutchAmount[]>([]);
  // (금액나눔 더치페이) 현재 선택된 금액 항목
  const selectedAmountId = ref<string | null>(null);
  const listRefs = ref();
  const itemRefs = ref();
  const keypad = ref('');

  // 정렬된 더치페이 금액 항목
  const sortedDutchMenus = computed(() =>
    dutchAmounts.value.slice().sort((a, b) => {
      if (a.IsPaid === b.IsPaid) {
        return 0;
      }
      return a.IsPaid ? 1 : -1;
    }),
  );

  // 이전 버튼 비활성화 여부
  const isPrevButtonDisabled = computed(() => dutchAmounts.value.some((amount) => amount.IsPaid));

  // 결제 완료되었거나 고정된 금액 항목을 제외하고 남은 결제 금액
  const remainingDutchAmount = computed(() => {
    const paidAmount = dutchAmounts.value
      .filter((amount) => amount.IsPaid && amount.IsChanged)
      .reduce((acc, amount) => acc + amount.TotalPrice, 0);
    return paymentStore.summary.amount - paidAmount;
  });

  // 선택한 더치페이 금액 항목
  const selectedAmount = computed(() => {
    if (selectedAmountId.value) {
      return dutchAmounts.value.find((amount) => amount.Id === selectedAmountId.value);
    } else {
      return dutchAmounts.value.find((amount) => !amount.IsPaid);
    }
  });

  watch(
    () => paymentStore.remainingAmount,
    async (newVal) => {
      // 포인트 결제 후 남은 결제 금액이 0원이 된 경우 결제 완료 처리
      if (newVal === 0) {
        // 주문 전송
        await order();
      }
    },
  );

  onMounted(() => {
    // 초기 데이터 설정
    dutchAmounts.value = new Array({
      Id: uuid(),
      IsPaid: false,
      IsChanged: false,
      TotalPrice: paymentStore.summary.amount,
    } satisfies DutchAmount);

    logger.info('[PaymentDutchAmount] 더치페이 금액나눔 초기화', dutchAmounts.value);
  });

  /**
   * 금액 항목 추가
   * @param dutchAmount 더치페이 금액 데이터
   */
  const addDutchAmounts = (dutchAmount: DutchAmount) => {
    dutchAmounts.value.unshift(dutchAmount);
  };

  /**
   * 금액 항목 선택
   * @param id 금액 항목 아이디
   */
  const setSelectedAmountId = (id: string | null) => {
    const isPaid = dutchAmounts.value.find((amount) => amount.Id === id)?.IsPaid;
    if (!isPaid) {
      selectedAmountId.value = id;
    }
  };

  /**
   * 현재 선택되어있는 금액 항목 삭제
   * @param id 금액 항목 아이디
   */
  const removeDutchAmount = (id: string) => {
    if (dutchAmounts.value.filter((amount) => !amount.IsPaid).length === 1) return;
    const index = dutchAmounts.value.findIndex((amount) => amount.Id === id);
    if (index > -1) dutchAmounts.value.splice(index, 1);
    // 항목 선택해제
    setSelectedAmountId(null);
  };

  /**
   * 현재 생성되어 있는 금액 항목 개수에 맞춰 금액 계산
   */
  const divideAmount = () => {
    // 결제 완료되었거나 고정된 금액 항목을 제외한 남은 결제 금액
    const remainingItemAmount =
      paymentStore.summary.amount -
      dutchAmounts.value
        .filter((amount) => amount.IsPaid || amount.IsChanged)
        .reduce((acc, amount) => acc + amount.TotalPrice, 0);

    // 나눈 금액을 설정할 항목 필터링
    const remainingItems = dutchAmounts.value.filter((amount) => !amount.IsPaid && !amount.IsChanged);

    remainingItems.forEach((amount, idx) => {
      amount.IsChanged = false;
      amount.TotalPrice = Math.floor(remainingItemAmount / remainingItems.length);
      if (idx === 0) {
        amount.TotalPrice += remainingItemAmount % remainingItems.length;
      }
    });
  };

  /**
   * 결제완료 데이터 세팅
   */
  const setDutchAmountOrderFlag = () => {
    if (selectedAmount.value) {
      selectedAmount.value.IsPaid = true;
      setSelectedAmountId(null);
    }
  };

  /** 더치페이 남은 금액 항목으로 추가 */
  const addRestAmount = () => {
    // 현재 생성된 아이템 합계 금액
    const itemTotalAmount = dutchAmounts.value.reduce((acc, amount) => acc + amount.TotalPrice, 0);

    // 현재 생성된 아이템 합계 금액이 전체 결제 금액보다 크거나 같으면 추가하지 않음
    if (itemTotalAmount >= paymentStore.summary.amount) return;

    // 남는 금액이 있다면 자동으로 항목 추가
    addDutchAmounts({
      Id: uuid(),
      IsChanged: false,
      IsPaid: false,
      TotalPrice: paymentStore.summary.amount - itemTotalAmount,
    } satisfies DutchAmount);
  };

  /**
   * 키패드 클릭
   * @param keyValue 키 값
   */
  const handleKeypadClick = (keyValue: string) => {
    // 선택된 항목이 있는지 검사
    if (!selectedAmount.value) return;

    if (keyValue === 'C') {
      selectedAmount.value.TotalPrice = 0;
    } else if (keyValue === 'B') {
      selectedAmount.value.TotalPrice = Number(
        selectedAmount.value.TotalPrice.toString().slice(0, selectedAmount.value.TotalPrice.toString().length - 1),
      );
    } else {
      selectedAmount.value.TotalPrice = Number(selectedAmount.value.TotalPrice.toString() + keyValue);
    }

    // 고정 금액 조회 (금액이 고정이고, 결제가 완료되지 않았고, 현재 선택된 항목이 아닌 금액 항목들의 합)
    const fixedAmount = dutchAmounts.value
      .filter((amount) => amount.IsChanged && !amount.IsPaid && amount.Id !== selectedAmount.value?.Id)
      .reduce((acc, amount) => acc + amount.TotalPrice, 0);

    // 입력 가능한 최대 금액 = 남은 결제 금액 - 고정금액
    const maxAmount = paymentStore.remainingAmount - fixedAmount;

    // 남은 합계 금액보다 입력한 금액이 더 클 경우
    if (selectedAmount.value.TotalPrice > maxAmount) {
      selectedAmount.value.TotalPrice = Number(
        selectedAmount.value.TotalPrice.toString().slice(0, selectedAmount.value.TotalPrice.toString().length - 1),
      );
      commonStore.showDialog({ type: 'alert', message: t('error.amount_exceed') });
      return;
    }

    selectedAmount.value.IsChanged = true;
    divideAmount();
  };

  /** 이전 버튼 클릭 */
  const handlePrevClick = () => modalStore.goBackFullScreenModal();

  /** 인원수 추가 */
  const handleAddClick = () => {
    const amount: DutchAmount = {
      Id: uuid(),
      IsPaid: false,
      IsChanged: false,
      TotalPrice: 0,
    };
    addDutchAmounts(amount);

    // 추가된 인원 수에 맞춰 금액 나눔
    divideAmount();

    // List에 Item 추가로 인해 DOM 업데이트 시에 스크롤이 맨 아래로 이동하는 현상을 막기 위한 코드
    nextTick(() => {
      listRefs.value.scrollTop = 0;
    });
  };

  /**
   * 더치페이 금액 항목 선택
   * @param id 선택한 더치페이 금액 항목 고유 아이디
   */
  const handleItemClick = (id: string) => {
    setSelectedAmountId(id);
  };

  /**
   * 더치페이 금액 항목 삭제 선택
   * @param id 선택한 더치페이 금액 항목 고유 아이디
   */
  const handleRemoveClick = (id: string) => {
    // 선택된 항목 삭제
    removeDutchAmount(id);
    // 줄어든 항목에 맞춰 금액 나눔
    divideAmount();
  };

  /**
   * 더치페이 금액 항목 현금 결제 선택
   * @param id 선택한 더치페이 금액 항목 고유 아이디
   */
  const handleCashClick = async (id: string) => {
    logger.info('[PaymentDutchAmount] 현금 결제 선택');
    setSelectedAmountId(id);

    // 선택한 항목의 금액 조회
    const amount = selectedAmount.value?.TotalPrice;

    // 금액 유효성 검사
    if (!amount) throw new UserError(t('error.required_amount'));

    const isOK = await commonStore.showDialog({
      type: 'confirm',
      title: t('label.take_order'),
      message: t('general.cash_pay_message'),
    });
    if (!isOK) return;

    // 결제 데이터를 현금 결제로 주문에 추가
    paymentStore.addPaymentSplit(createPaymentSplit(PaymentMethod.Cash, amount));

    // 현재 선택된 항목을 현금결제 처리
    setDutchAmountOrderFlag();

    // 화면에 표시된 금액항목의 합이 결제 금액보다 작다면 나머지 금액을 신규 금액항목으로 추가
    addRestAmount();
  };

  const handleCardClick = async (id: string) => {
    logger.info('[PaymentDutchAmount] 카드 결제 선택');
    setSelectedAmountId(id);

    // 선택한 항목의 금액 조회
    const amount = selectedAmount.value?.TotalPrice;

    // 금액 유효성 검사
    if (!amount) throw new UserError(t('error.required_amount'));

    // 결제 데이터 설정
    const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentCardModal, boolean>(PaymentCardModal, {
      amount,
    });
    if (!isSuccess) return;

    // 더치페이 금액 항목을 카드 결제로 완료 처리
    setDutchAmountOrderFlag();

    // 화면에 표시된 금액항목의 합이 결제 금액보다 작다면 나머지 금액을 신규 금액항목으로 추가
    addRestAmount();
  };

  /**
   * 간편 결제 클릭
   * @param id 결제하려는 dutch amount id
   */
  const handleEasyClick = async (id: string) => {
    logger.info('[PaymentDutchAmount] 간편 결제 선택');
    setSelectedAmountId(id);

    // 선택한 항목의 금액 조회
    const amount = selectedAmount.value?.TotalPrice;

    // 금액 유효성 검사
    if (!amount) throw new UserError(t('error.required_amount'));

    // 간편 결제 진행 후 성공 여부 반환
    const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentEasyModal, boolean>(PaymentEasyModal, {
      amount,
    });

    // 간편 결제 실패한 경우 종료
    if (!isSuccess) return;

    // 더치페이 금액 항목을 카드 결제로 완료 처리
    setDutchAmountOrderFlag();

    // 화면에 표시된 금액항목의 합이 결제 금액보다 작다면 나머지 금액을 신규 금액항목으로 추가
    addRestAmount();
  };

  /** 주문하기 */
  const order = async () => {
    logger.info('[PaymentDutchAmount] 주문 전송 시작');
    const loadingId = commonStore.showLoading();
    try {
      // POS에서 주문 조회 후 세팅
      await orderStore.fetchOrder(true);

      // 선결제인 경우 장바구니 데이터를 주문 데이터에 추가
      if (!orderStore.isPreOrder) {
        orderStore.addOrderItem(cartStore.cart.CartMenus);
      }

      // 모든 결제가 현금인지 여부 (주문 전송 후 결제 데이터 초기화 되기 때문에 주문 전송 이전에 위치해야함)
      const isCashOnly = paymentStore.summary.splits.every((split) => split.method === PaymentMethod.Cash);

      // 주문 전송
      await orderStore.sendOrder(true);

      // 모든 결제가 현금 결제인 경우 영수증 요청 생략하고 결제완료 모달 열기. 다른 결제가 있으면 영수증 모달 열기.
      if (isCashOnly) {
        modalStore.openFullScreenModal(OrderComplete, { hasPayment: false });
      } else {
        // 영수증 또는 주문완료 모달 열기
        openReceiptOrComplete(false);
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .payment-dutch {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto 1fr auto;
    height: 100vh;
    padding: 2.2388vw 2.8358vw 0;
    @media (max-width: vars.$sm) {
      grid-template-columns: 1fr;
      grid-template-rows: auto 2fr 1fr auto;
    }
    .dutch-amount__list {
      grid-column: 1 / 3;
      padding: 0.7463vw 0.7463vw 0.7463vw 0;
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      @media (max-width: vars.$sm) {
        grid-column: 1 / 1;
      }
      .payment-group__ul {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.4925vw;
        flex-wrap: wrap;
        margin-bottom: -1.6418vw;
        li {
          min-height: 11.9403vw;
          @media (max-width: vars.$sm) {
            min-height: 76px;
          }
        }
      }
    }

    .dutch-amount__keypad {
      grid-column: 3 / 4;
      display: flex;
      align-items: center;
      width: 100%;
      padding: 2.9851vw 1.4925vw;
      @media (max-width: vars.$sm) {
        grid-column: 1 / 1;
      }
    }

    .dutch-amount__footer {
      grid-column: 1 / 4;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: auto -2.8358vw 0 -2.8358vw;
      box-shadow: inset 0 0.2239vw 0.4478vw #8a8a8a29;
      padding: 1.4925vw;
      background: #f8f8f8;
      @media (max-width: vars.$sm) {
        grid-column: 1 / 3;
      }
      .dutch-amount__info {
        display: flex;
        gap: 1.4925vw;
        justify-content: space-between;
        width: 59.7015vw;
        @media (max-width: vars.$sm) {
          flex: 2;
        }
        .info__qty {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          width: 8.9552vw;
          flex: 0 0 8.9552vw;
          margin-right: 2.9851vw;
          border-bottom: 0.0746vw solid var(--border-color-primary);
        }
        .info__amount {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          flex: 1 1 0;
          border-bottom: 0.0746vw solid var(--border-color-primary);
        }
        .info__text {
          font-size: 1.4925vw;
          font-weight: 500;
          @media (max-width: vars.$md) {
            font-size: 14px;
          }
        }
        .info__number {
          font-size: 3.7313vw;
          font-weight: 700;
          @media (max-width: vars.$md) {
            font-size: 24px;
          }
        }
        .info__number--red {
          color: var(--point-color-secondary);
        }
      }
      .dutch-amount__button-group {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        width: 22.3881vw;
        padding-left: 2.9851vw;
        @media (max-width: vars.$sm) {
          flex: 1;
        }
        .BaseButton + .BaseButton {
          margin-left: 0.7463vw;
        }
      }
    }
  }

  .dark-theme,
  .dark-gray-theme {
    .payment-dutch {
      .BaseButton--black {
        background: #000;
        span {
          color: var(--brand-secondary);
        }
      }
    }
  }
</style>
